/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Welcome", description = "Introduction to Hightouch for marketers and data teams. Learn what the platform does, how each team uses it, and how to get started with audience building, data activation, and campaign measurement.", lastUpdatedDate = new Date(1755043200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Welcome",
        description: "Introduction to Hightouch for marketers and data teams. Learn what the platform does, how each team uses it, and how to get started with audience building, data activation, and campaign measurement.",
        lastUpdatedDate: "Wed Aug 13 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "What is Hightouch?",
          "depth": 0,
          "id": "what-is-hightouch"
        }, {
          "value": "What you can do with Hightouch",
          "depth": 0,
          "id": "what-you-can-do-with-hightouch"
        }, {
          "value": "Platform overview",
          "depth": 0,
          "id": "platform-overview"
        }, {
          "value": "Core components",
          "depth": 1,
          "id": "core-components"
        }, {
          "value": "Optional add-ons",
          "depth": 1,
          "id": "optional-add-ons"
        }, {
          "value": "How marketers work in Hightouch",
          "depth": 0,
          "id": "how-marketers-work-in-hightouch"
        }, {
          "value": "1. Explore available data",
          "depth": 1,
          "id": "1-explore-available-data"
        }, {
          "value": "2. Build and preview audiences",
          "depth": 1,
          "id": "2-build-and-preview-audiences"
        }, {
          "value": "3. Sync audiences to tools",
          "depth": 1,
          "id": "3-sync-audiences-to-tools"
        }, {
          "value": "4. Test and optimize campaigns",
          "depth": 1,
          "id": "4-test-and-optimize-campaigns"
        }, {
          "value": "5. Analyze and improve",
          "depth": 1,
          "id": "5-analyze-and-improve"
        }, {
          "value": "How data teams benefit",
          "depth": 0,
          "id": "how-data-teams-benefit"
        }, {
          "value": "Learn the basics",
          "depth": 0,
          "id": "learn-the-basics"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      strong: "strong",
      img: "img",
      ul: "ul",
      li: "li",
      h3: "h3",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      br: "br"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "what-is-hightouch",
        children: "What is Hightouch?"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch is a ", _jsx(_components.strong, {
          children: "composable customer data platform (CDP)"
        }), " that connects your data warehouse directly to the tools your teams use."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Data teams"
        }), " prepare and govern trusted datasets. ", _jsx(_components.strong, {
          children: "Marketers"
        }), " self-serve those datasets to build audiences, launch campaigns, and measure performance."]
      }), "\n", _jsx(_components.p, {
        children: "Unlike traditional CDPs, Hightouch doesn’t copy or store data in a separate system. It runs on top of your existing infrastructure, giving marketers real-time access while letting data teams maintain control."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch is ", _jsx(_components.strong, {
          children: "modular"
        }), ". You can adopt components as you need them, such as real-time event triggers, identity resolution, or AI-based campaign optimization."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/product-suite.png",
          alt: "Product components"
        })
      }), "\n", _jsx(_components.h2, {
        id: "what-you-can-do-with-hightouch",
        children: "What you can do with Hightouch"
      }), "\n", _jsx(_components.p, {
        children: "With Hightouch, you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Build and sync customer audiences"
          }), " from live warehouse data without SQL"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Automate marketing workflows"
          }), " by triggering actions when audiences change"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Unify customer identities"
          }), " into a consistent source of truth for personalization and reporting"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Enrich audience lists"
          }), " with identifiers like emails or phone numbers to improve match rates"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Deliver timely campaigns"
          }), " by syncing data on a schedule or in response to real-time events"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Let AI optimize delivery decisions"
          }), " like message timing, content, and channel"]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Measure performance"
          }), " across audiences and traits using built-in charts and dashboards"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/marketing-overview.png",
          alt: "Overview"
        })
      }), "\n", _jsx(_components.h2, {
        id: "platform-overview",
        children: "Platform overview"
      }), "\n", _jsx(_components.h3, {
        id: "core-components",
        children: "Core components"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Component"
            }), _jsx(_components.th, {
              children: "Used for"
            }), _jsx(_components.th, {
              children: "Example use"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/getting-started/concepts",
                children: _jsx(_components.strong, {
                  children: "Reverse ETL"
                })
              })
            }), _jsx(_components.td, {
              children: "Send modeled data to destinations like ad platforms or CRMs"
            }), _jsx(_components.td, {
              children: "Sync churn-risk users from Snowflake to Braze for reactivation"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/customer-studio/overview",
                children: _jsx(_components.strong, {
                  children: "Customer Studio"
                })
              })
            }), _jsx(_components.td, {
              children: "Build audiences, journeys, and analyze results"
            }), _jsx(_components.td, {
              children: "Launch a win-back journey for users inactive in the past 30 days"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "optional-add-ons",
        children: "Optional add-ons"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Component"
            }), _jsx(_components.th, {
              children: "Used for"
            }), _jsx(_components.th, {
              children: "Example use"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/events/overview",
                children: _jsx(_components.strong, {
                  children: "Events"
                })
              })
            }), _jsx(_components.td, {
              children: "Trigger workflows from real-time user actions"
            }), _jsx(_components.td, {
              children: "Trigger a cart abandonment email when a user leaves checkout without purchase"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/match-booster/overview",
                children: _jsx(_components.strong, {
                  children: "Match Booster"
                })
              })
            }), _jsx(_components.td, {
              children: "Enrich user lists to improve match rates"
            }), _jsx(_components.td, {
              children: "Add phone numbers to an email list before syncing to Meta"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/identity-resolution/beta/overview",
                children: _jsx(_components.strong, {
                  children: "Identity Resolution"
                })
              })
            }), _jsx(_components.td, {
              children: "Unify customer profiles across sources"
            }), _jsx(_components.td, {
              children: "Combine web and app activity into a single user profile"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/ai-decisioning/overview",
                children: _jsx(_components.strong, {
                  children: "AI Decisioning"
                })
              })
            }), _jsx(_components.td, {
              children: "Optimize message, timing, and channel per user"
            }), _jsx(_components.td, {
              children: "Let AI decide between sending an email or SMS based on past engagement"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://hightouch.com/docs/campaign-intelligence/overview",
                children: _jsx(_components.strong, {
                  children: "Intelligence"
                })
              })
            }), _jsx(_components.td, {
              children: "Analyze campaigns and measure ROI"
            }), _jsx(_components.td, {
              children: "Compare campaign performance across audience segments"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "how-marketers-work-in-hightouch",
        children: "How marketers work in Hightouch"
      }), "\n", _jsx(_components.h3, {
        id: "1-explore-available-data",
        children: "1. Explore available data"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Browse ", _jsx(_components.strong, {
            children: "traits"
          }), ", ", _jsx(_components.strong, {
            children: "events"
          }), ", and ", _jsx(_components.strong, {
            children: "relationships"
          }), " prepared by your data team. Use these building blocks to create segments based on behavior, profile attributes, or lifecycle stage."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/customer-studio-snowflake.gif",
          alt: "Customer Studio"
        })
      }), "\n", _jsx(_components.h3, {
        id: "2-build-and-preview-audiences",
        children: "2. Build and preview audiences"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Define filters using a ", _jsx(_components.strong, {
              children: "visual, no-code interface"
            }), ".", _jsx(_components.br, {}), "\n", _jsx(_components.img, {
              src: "get-started/build-an-audience.gif",
              alt: "Audience builder"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["See your ", _jsx(_components.strong, {
              children: "audience size and member list"
            }), " update in real time as you adjust conditions.\n", _jsx(_components.img, {
              src: "get-started/insights.gif",
              alt: "Insights"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "3-sync-audiences-to-tools",
        children: "3. Sync audiences to tools"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.strong, {
              children: "Send audiences"
            }), " to CRMs, email platforms, ad tools, or push notification systems.", _jsx(_components.br, {}), "\n", _jsx(_components.img, {
              src: "get-started/retl_platform_databricks.gif",
              alt: "Syncs"
            })]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Use built-in guardrails to ", _jsx(_components.strong, {
              children: "manage sync behavior and control where data goes"
            }), ".\n", _jsx(_components.img, {
              src: "/hightouch-audiences/destination-rules/cs-dr-new-rule.png",
              alt: "Destination rule definition"
            })]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "4-test-and-optimize-campaigns",
        children: "4. Test and optimize campaigns"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Create audience splits"
          }), " or holdouts to run controlled tests.", _jsx(_components.br, {}), "\n", _jsx(_components.img, {
            src: "campaign-intel/splits/ci-splits-example.png",
            alt: "Splits view"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "5-analyze-and-improve",
        children: "5. Analyze and improve"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Compare performance"
          }), " across groups to learn what works best."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Use charts and dashboards"
          }), " to explore audience traits and campaign outcomes."]
        }), "\n", _jsxs(_components.li, {
          children: ["Track changes over time, spot drop-off points, and ", _jsx(_components.strong, {
            children: "uncover insights to refine your strategy"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "campaign-intel/splits/splits-chart-normalized-per-member.png",
          alt: "Dashboard example"
        })
      }), "\n", _jsx(_components.h2, {
        id: "how-data-teams-benefit",
        children: "How data teams benefit"
      }), "\n", _jsx(_components.p, {
        children: "Data teams handle a one-time setup to define the datasets marketers use in Hightouch. You can use SQL, dbt, or the visual model builder to expose traits, events, and relationships in a structured, reusable format."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/schema-builder.gif",
          alt: "Schema setup"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Once models are in place"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "You don't need to maintain separate pipelines for each marketing tool"
        }), "\n", _jsx(_components.li, {
          children: "Data remains in your warehouse—no duplication into a third-party CDP"
        }), "\n", _jsx(_components.li, {
          children: "Governance and access control are enforced at the source"
        }), "\n", _jsx(_components.li, {
          children: "Marketers can self-serve trusted data, reducing ad hoc requests and tickets"
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "learn-the-basics",
        children: "Learn the basics"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.a, {
              href: "https://hightouch.com/docs/customer-studio/overview",
              children: _jsx(_components.strong, {
                children: "Customer Studio overview →"
              })
            }), _jsx(_components.br, {}), "\n", "Explore how marketers use Hightouch to build audiences, launch journeys, and track performance—without writing code."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.a, {
              href: "https://hightouch.com/docs/getting-started/concepts",
              children: _jsx(_components.strong, {
                children: "Data activation overview →"
              })
            }), _jsx(_components.br, {}), "\n", "Learn how source data powers models and syncs. Understand the building blocks of Reverse ETL, including sources, models, destinations, and syncs."]
          }), "\n"]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
